import { useCallback, useMemo, useState } from 'react';
import { mindtimeWazuhThreatHuntingApi } from 'src/api/mindtime/wazuhThreatHuntingApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import logger from 'src/utils/logger';
import { getThreatHuntingPreviewUserOptions } from '../preview/threatHunting';
import type { SecuritySearchableFilterOption } from '../shared/SecuritySearchableFilter';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

type UseThreatHuntingUserFilterOptionsArgs = {
  pageOrganizationId?: string;
  /** Comma-separated wazuhAgentId values from the Endpoint filter. */
  wazuhAgentId?: string;
  systemWazuhAgentId?: string;
  /** Override preview gate (e.g. endpoint tab uses useEdrDataGate('endpoints')). */
  isPreviewMode?: boolean;
};

/** GET /api/v1/threat-hunting/users — searchable User filter options. */
export const useThreatHuntingUserFilterOptions = ({
  pageOrganizationId,
  wazuhAgentId: wazuhAgentIdArg,
  systemWazuhAgentId,
  isPreviewMode: previewModeOverride,
}: UseThreatHuntingUserFilterOptionsArgs = {}) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode: gatePreviewMode } = useEdrDataGate('threat-hunting');
  const isPreviewMode = previewModeOverride ?? gatePreviewMode;
  const profileOrgId = userData?.organization?.id;
  const [options, setOptions] = useState<SecuritySearchableFilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const wazuhAgentId = selectedEndpointId?.trim() || wazuhAgentIdArg;

  const previewUserOptions = useMemo(
    () => getThreatHuntingPreviewUserOptions(systemWazuhAgentId),
    [systemWazuhAgentId]
  );

  const listParams = useMemo(() => {
    const agentId = systemWazuhAgentId?.trim();
    if (agentId) {
      return { systemWazuhAgentId: agentId };
    }
    return {
      ...buildEdrApiScope({
        scope,
        profileOrgId,
        isReseller,
        pageOrganizationId: pageOrganizationId || undefined,
      }),
      ...(wazuhAgentId?.trim() ? { wazuhAgentId: wazuhAgentId.trim() } : {}),
    };
  }, [scope, profileOrgId, isReseller, pageOrganizationId, wazuhAgentId, systemWazuhAgentId]);

  const onSearch = useCallback(
    async (query: string) => {
      if (isPreviewMode) {
        const q = query.trim().toLowerCase();
        setOptions(
          q
            ? previewUserOptions.filter((option) => option.label.toLowerCase().includes(q))
            : previewUserOptions
        );
        return;
      }

      setLoading(true);
      try {
        const users = await mindtimeWazuhThreatHuntingApi.getUsers({
          ...listParams,
          search: query.trim() || undefined,
          pageSize: 100,
        });
        setOptions(users.map((name) => ({ label: name, value: name })));
      } catch (error) {
        logger(error, 'error', 'useThreatHuntingUserFilterOptions');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [isPreviewMode, listParams, previewUserOptions]
  );

  return { options, loading, onSearch };
};
